@if (currentJob(); as j) {
  <button
    type="button"
    (click)="onClick()"
    class="w-full text-left rounded-lg border p-3 sm:p-4 transition-colors"
    [class.border-blue-500]="isActive()"
    [class.bg-blue-50]="isActive()"
    [class.border-slate-200]="!isActive()"
    [class.hover:bg-slate-50]="!isActive()"
    [attr.data-active]="isActive()"
    [attr.data-job-id]="j.id"
  >
    <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
      <!-- Left: source / actor / time / counts -->
      <div class="flex flex-col gap-1 md:flex-row md:items-center md:gap-3 min-w-0">
        <div class="flex items-center gap-2">
          <span
            class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-700"
            aria-hidden="true"
          >
            <!-- Generic source icon (document) -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.75"
              stroke="currentColor"
              class="h-4 w-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5A3.375 3.375 0 0 0 10.125 2.25H8.25M9 16.5h6m-6-3h6m-7.5 6h9a2.25 2.25 0 0 0 2.25-2.25V9.776A3.75 3.75 0 0 0 15.75 7.5h-1.875A1.125 1.125 0 0 1 12.75 6.375V4.5A3.75 3.75 0 0 0 9 .75H6.75A2.25 2.25 0 0 0 4.5 3v15.75A2.25 2.25 0 0 0 6.75 21Z"
              />
            </svg>
          </span>
          @if (editing()) {
            <input
              #titleInput
              [ngModel]="editValue()"
              (ngModelChange)="editValue.set($event)"
              (blur)="saveEdit()"
              (keydown.enter)="saveEdit()"
              (keydown.escape)="cancelEdit()"
              (click)="$event.stopPropagation()"
              class="text-sm font-semibold text-gray-900 border border-blue-400 rounded px-1 py-0.5 w-full min-w-0"
              data-testid="job-title-input"
            />
          } @else {
            <span
              class="group inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
              (click)="startEdit($event)"
              data-testid="job-title"
            >
              {{ cardTitle() }}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"
                />
              </svg>
            </span>
          }
        </div>

        <span class="hidden md:inline text-slate-300" aria-hidden="true">·</span>

        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 min-w-0">
          <span>{{ relativeTime() }}</span>
          <span class="text-slate-300" aria-hidden="true">·</span>
          <span>{{ j.actorDisplayName }}</span>
          @if (isOwn()) {
            <span
              class="inline-flex items-center rounded-full bg-blue-100 px-1.5 py-1 -ml-1 text-[10px] font-medium text-blue-700 leading-none"
              >YOU</span
            >
          }
          <span class="text-slate-300" aria-hidden="true">·</span>
          <span>{{ j.itemsAdded }} added</span>
          @if (j.papersSkipped > 0) {
            <span class="text-slate-300" aria-hidden="true">·</span>
            <span class="text-slate-500" data-testid="job-duplicates"
              >{{ j.papersSkipped }} duplicates</span
            >
          }
          @if (j.papersMalformed > 0) {
            <span class="text-slate-300" aria-hidden="true">·</span>
            <span class="text-red-600" data-testid="job-malformed"
              >{{ j.papersMalformed }} malformed</span
            >
          }
          @if (j.status !== 'RUNNING' && j.itemsNeedingAttention > 0) {
            <span class="text-slate-300" aria-hidden="true">·</span>
            <span class="text-amber-700">{{ j.itemsNeedingAttention }} need attention</span>
          }
        </div>
      </div>

      <!-- Right: undo button + status badge + detail button -->
      <div class="flex flex-shrink-0 items-center gap-1">
        @if (showUndo()) {
          <button
            type="button"
            (click)="onUndo($event)"
            class="inline-flex h-6 items-center gap-1 rounded-md px-2 mr-4 text-xs font-medium text-red-600 hover:bg-red-50 border border-red-200"
            aria-label="Undo import"
            data-testid="job-undo-button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              class="h-3.5 w-3.5"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
              />
            </svg>
            Undo
          </button>
        }
        <app-status-badge [variant]="j.status"></app-status-badge>
        <!-- Detail modal shows per-paper status and failed uploads. -->
        @if (j.source === 'PDF' || j.source === 'BIBTEX') {
          <button
            type="button"
            (click)="onDetail($event)"
            class="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Job details"
            data-testid="job-detail-button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              class="h-5 w-5"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
              />
            </svg>
          </button>
        }
      </div>
    </div>

    <!-- Descriptor line (truncated) -->
    @if (j.descriptor) {
      <p class="mt-2 text-xs text-gray-600 truncate" [attr.title]="j.descriptor">
        {{ j.descriptor }}
      </p>
    }
    <!-- Type filter recap line -->
    @if (typeFilterLine(); as line) {
      <p class="mt-1 text-xs text-slate-500" data-testid="job-type-filter-line">{{ line }}</p>
    }
    @if (readyLabel()) {
      <span class="text-xs text-slate-500" data-testid="job-ready">{{ readyLabel() }}</span>
    }

    @if (j.status === 'FAILED' && j.failureReason; as reason) {
      <p class="mt-1 text-xs text-red-600" data-testid="job-failure-reason">{{ reason }}</p>
    }

    <!-- Determinate fetch-progress bar for a RUNNING OpenAlex import. -->
    @if (showProgress()) {
      <div class="mt-2" data-testid="job-progress">
        <div class="mb-1 flex items-center justify-between text-xs text-gray-600">
          <span>Importing…</span>
          <span data-testid="job-progress-label">{{ progressLabel() }}</span>
        </div>
        <div class="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            class="h-full rounded-full bg-blue-500 transition-all"
            data-testid="job-progress-bar"
            role="progressbar"
            [style.width.%]="progressPercent()"
            [attr.aria-valuenow]="progressPercent()"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
    }
  </button>
}
